import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { config } from '../config';
import { AuthService } from './auth.service';
import Swal from 'sweetalert2';

@Injectable({
    providedIn: 'root'
})
export class MagicLinkService {

    constructor(
        private http: HttpClient,
        private router: Router,
        private authService: AuthService
    ) { }

    /**
     * Send magic link email
     */
    sendMagicLinkEmail(email: string, userName: string, redirectUrl?: string) {
        const formData = new FormData();
        formData.append('email', email);
        formData.append('user_name', userName);
        formData.append('type', 'magic_link');
        // Optional URL used by backend to send the user to the right place after clicking the email link.
        if (redirectUrl && redirectUrl.trim() !== '') {
            formData.append('redirect_url', redirectUrl);
        }

        return this.http.post(config.baseUrl + 'supersee/v1/login-code', formData);
    }

    /**
     * Verify magic link token
     */
    verifyMagicLinkToken(loginCode: string, email?: string) {
        const formData = new FormData();
        formData.append('login_code', loginCode);

        if (email && email.trim() !== '') {
            formData.append('email', email);
        }

        return this.http.post(config.baseUrl + 'supersee/v1/login-code/verify', formData);
    }

    /**
     * Process magic link authentication
     */
    processMagicLinkAuthentication(loginCode: string, email?: string, intent?: string) {
        this.verifyMagicLinkToken(loginCode, email).subscribe(
            (response: any) => {
                console.log('Magic link verification response:', response);
                console.log('Response structure analysis:', {
                    hasUser: !!response.user,
                    userKeys: response.user ? Object.keys(response.user) : 'No user object',
                    topLevelKeys: Object.keys(response),
                    idValue: response.id,
                    userIdValue: response.user?.id,
                    companyIdValue: response.company_id,
                    userCompanyIdValue: response.user?.company_id
                });

                if (response && response.token) {
                    // Create user data object for auth service
                    const userData = {
                        token: response.token,
                        user_email: response.user_email || response.email,
                        user_display_name: response.user_display_name || response.display_name || response.name,
                        role: response.role || 'user',
                        isVerified: response.isVerified !== false,
                        verified: response.verified || response.isVerified || '1',
                        // Prioritize actual user ID over email
                        id: response.user?.id || response.id || response.user?.user_id || response.user_id || response.user?.ID || response.ID,
                        // Prioritize actual company ID over email
                        company_id: response.user?.company_id || response.company_id || response.user?.company_id || response.company_id,
                        name: response.user?.name || response.name || response.user_display_name || response.display_name,
                        email: response.user?.email || response.email || response.user_email,
                        username: response.user?.username || response.username || response.user_email || response.email
                    };

                    // If we still don't have proper IDs, log a warning
                    // if (!userData.id || userData.id.includes('@')) {
                    //     console.warn('Magic link response missing proper user ID, this may cause Firebase path issues');
                    // }
                    // if (!userData.company_id || userData.company_id.includes('@')) {
                    //     console.warn('Magic link response missing proper company ID, this may cause Firebase path issues');
                    // }

                    // Login chooses the next page after /users/me is stored.
                    // Navigating here as well races that redirect and the setup guard
                    // sends a new signup to an empty dashboard.
                    this.authService.handleSuccessfulLogin(userData, false, false);

                    console.log('Created user data for magic link:', userData);

                } else {
                    // Show detailed error for invalid response
                    let errorDetails = 'Invalid response from server.';
                    if (response) {
                        errorDetails += ` Response keys: ${Object.keys(response).join(', ')}`;
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Invalid Response',
                        html: `
                            <p><strong>Server returned invalid response</strong></p>
                            <p style="font-size: 0.9em; color: #666; margin-top: 10px;">${errorDetails}</p>
                            <p style="margin-top: 15px;">Please try again or login with your credentials.</p>
                        `,
                        confirmButtonText: 'Go to Login',
                        confirmButtonColor: '#667eea'
                    }).then(() => {
                        this.router.navigate(['/login']);
                    });
                }
            },
            (error) => {
                console.error('Magic link verification failed:', error);

                // Extract the specific error message from the backend
                let errorMessage = 'Magic link verification failed.';

                if (error.error && error.error.message) {
                    errorMessage = error.error.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                // Show user-friendly error alert without technical details
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Failed',
                    html: `
                        <p><strong>${errorMessage}</strong></p>
                    `,
                    confirmButtonText: 'Go to Login',
                    confirmButtonColor: '#667eea',
                    showCloseButton: true
                }).then(() => {
                    this.router.navigate(['/login']);
                });
            }
        );
    }


}
